import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getCommonNumberFilterParams,
  getSetFilterParams,
  getSetDatefilterParams,
} from '../../utils/agGridFilters';
import { DateFormatter, DateTimeFormatter } from '../../shared/controls/formatters';

// deterministic hash → avatar palette (same approach as the other reskinned lists)
const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

// Sales-order status tone (admin-configurable strings → generic badge tones),
// mirroring the design's SO_STATUS map: created→blue, approved→green,
// delivered→purple, backordered→amber, cancelled→red.
const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('cancel') || s.includes('reject') || s.includes('void')) return 'red';
  if (s.includes('deliver')) return 'purple';
  if (s.includes('approv') || s.includes('complet') || s.includes('paid')) return 'green';
  if (s.includes('backorder') || s.includes('hold') || s.includes('pend')) return 'amber';
  if (s.includes('creat') || s.includes('draft') || s.includes('open')) return 'blue';
  return 'gray';
};

const OwnerCell = (params) => {
  const name = params.value;
  if (!name) return null;
  return (
    <div className="gos-owner-cell">
      <span className="gos-ava sm" style={{ background: avaColor(name) }}>
        {initials(name)}
      </span>
      <span className="gos-owner-name">{name}</span>
    </div>
  );
};

const StatusCell = (params) => {
  if (!params.value) return null;
  const tone = statusTone(params.value);
  return (
    <span className={'gos-badge gos-b-' + tone}>
      <span className="gos-pip" />
      {params.value}
    </span>
  );
};

// GrowthOS re-skin: no Actions column (matches Quotes + the CRM lists' checkbox
// convention). Code is a bold link to the Sales Order view page; edit/delete are
// reached from there. Cells restyled: Code link, Status badge, Owner avatar.
const useSalesColumnDefs = ({ gridData, owner }) => {
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const commonNumberFilterParams = useMemo(() => getCommonNumberFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Code',
        field: 'code',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) => {
          if (!params.value) return null;
          return (
            <Link
              to={`/sales/viewSalesOrder?id=${params?.data?.id}&pageNo=${gridData?.inputPayload?.pageNumber}`}
              state={{ ...params.data }}
              className="gos-lead-name"
            >
              {params.value}
            </Link>
          );
        },
      },
      {
        headerName: 'Subject',
        field: 'subject',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 140,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: StatusCell,
      },
      {
        headerName: 'Grand Total',
        field: 'grandTotal',
        width: 130,
        filter: 'agNumberColumnFilter',
        filterParams: commonNumberFilterParams,
      },
      {
        headerName: 'Opportunity Name',
        field: 'opportunityName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Contact Name',
        field: 'contactName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Name',
        field: 'accountName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Sales Order Owner',
        field: 'salesOwner',
        minWidth: 190,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
        cellRenderer: OwnerCell,
      },
      {
        headerName: 'Sales Order Date',
        field: 'orderDate',
        valueFormatter: DateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Due Date',
        field: 'dueDate',
        valueFormatter: DateFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Created Date Time',
        field: 'createdDateTime',
        minWidth: 200,
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: 'Currency',
        field: 'currency',
        width: 110,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Quote Name',
        field: 'quoteName',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Carrier',
        field: 'carrier',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
    ];
  }, [commonTextFilterParams, commonNumberFilterParams, filterParams, gridData, dateFilterParams, owner]);

  return columnDefs;
};

export default useSalesColumnDefs;
